import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getToken } from '../features/token/tokenSaver';
import { getAdvertiserAccount } from '../services/getAdvertiserAccount';
import { getAdvertiserImpressionsAndCampaigns } from '../services/getAdvertiserImpressionsCampaigns';
import { getAllScreensRealTimeStatus } from '../services/getScreensWithCoordinates';
import { getCampaignDetails } from '../services/getCampaignDetails';
import { getAdminScreenInfo } from '../services/getAdminScreenInfo';
import { getUserDashboardData } from '../services/getUserDashboardData';
import { queryKeys } from './queryClient';

function parseAdvertiserId(token) {
    if (!token) return null;
    try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        return decoded?.id ?? null;
    } catch {
        return null;
    }
}

export function useAdvertiserAccountQuery(options = {}) {
    const token = useSelector(getToken);
    return useQuery({
        queryKey: queryKeys.advertiserAccount,
        queryFn: async () => {
            const res = await getAdvertiserAccount(token);
            return res.data;
        },
        enabled: Boolean(token) && (options.enabled !== false),
        ...options,
    });
}

export function useFleetScreensQuery(options = {}) {
    const token = useSelector(getToken);
    const view = options.view || 'full';
    return useQuery({
        queryKey: queryKeys.fleetScreens(view),
        queryFn: async () => {
            const res = await getAllScreensRealTimeStatus(token);
            const data = res.data;
            if (data?.success && Array.isArray(data.screens)) {
                return data;
            }
            if (Array.isArray(data)) {
                return { success: true, screens: data, totalScreens: data.length, activeScreens: data.filter((s) => s.isActive).length };
            }
            return { success: true, screens: [], totalScreens: 0, activeScreens: 0 };
        },
        enabled: Boolean(token) && (options.enabled !== false),
        refetchInterval: options.refetchInterval,
        refetchIntervalInBackground: false,
        ...options,
    });
}

export function useAdvertiserHomeQuery(options = {}) {
    const token = useSelector(getToken);
    const advertiserId = options.advertiserId ?? parseAdvertiserId(token);
    return useQuery({
        queryKey: queryKeys.advertiserHome(advertiserId),
        queryFn: async () => {
            const res = await getAdvertiserImpressionsAndCampaigns(token, advertiserId);
            return res.data;
        },
        enabled: Boolean(token && advertiserId) && (options.enabled !== false),
        ...options,
    });
}

export function useCampaignDetailsQuery(adId, options = {}) {
    const token = useSelector(getToken);
    return useQuery({
        queryKey: queryKeys.campaignDetails(adId),
        queryFn: async () => {
            const res = await getCampaignDetails(token, adId);
            return res.data;
        },
        enabled: Boolean(token && adId) && (options.enabled !== false),
        ...options,
    });
}

export function useAdminScreenInfoQuery(screenId, options = {}) {
    const token = useSelector(getToken);
    return useQuery({
        queryKey: queryKeys.adminScreenInfo(screenId),
        queryFn: async () => {
            const res = await getAdminScreenInfo(token, screenId);
            return res.data;
        },
        enabled: Boolean(token && screenId) && (options.enabled !== false),
        ...options,
    });
}

export function useHostDashboardQuery(userId, options = {}) {
    const token = useSelector(getToken);
    return useQuery({
        queryKey: queryKeys.hostDashboard(userId),
        queryFn: async () => {
            const res = await getUserDashboardData(token, userId);
            return res.data;
        },
        enabled: Boolean(token && userId) && (options.enabled !== false),
        ...options,
    });
}

export function useScreenPricingQuery(options = {}) {
    const token = useSelector(getToken);
    return useQuery({
        queryKey: queryKeys.screenPricing,
        queryFn: async () => {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/advertiser/screen-pricing`, {
                headers: { Authorization: token },
            });
            const data = await res.json();
            if (!res.ok || !data?.success) {
                throw new Error(data?.error || 'Failed to load screen pricing');
            }
            return data;
        },
        enabled: Boolean(token) && (options.enabled !== false),
        staleTime: 5 * 60_000,
        ...options,
    });
}
